// #region Imports
import { defineComponent } from 'vue';
// #endregion
// #region Core
export default defineComponent({
    // #region Class
    name: 'g-splitter',
    // #endregion
    // #region Props
    props: {
        direction: {
            type: String,
            default: 'horizontal',
        },
        /** Full-state v-model (v-model) */
        modelValue: {
            type: Object,
            default: null,
        },
        /** Individual v-model:split */
        split: {
            type: Number,
            default: null,
        },
        /** Individual v-model:pane1Collapsed */
        pane1Collapsed: {
            type: Boolean,
            default: null,
        },
        /** Individual v-model:pane2Collapsed */
        pane2Collapsed: {
            type: Boolean,
            default: null,
        },
        initialSplit: {
            type: Number,
            default: 50,
        },
        collapsible: {
            type: Boolean,
            default: true,
        },
        pane1Collapsible: {
            type: Boolean,
            default: true,
        },
        pane2Collapsible: {
            type: Boolean,
            default: true,
        },
        minPaneSize: {
            type: Number,
            default: 5, // percent
        },
        pane1Visible: {
            type: null,
            default: null,
        },
        pane2Visible: {
            type: null,
            default: null,
        },
        hideDividerWhenCollapsed: {
            type: Boolean,
            default: false,
        },
    },
    // #endregion
    // #region Emits
    emits: {
        'update:modelValue': (_val) => true,
        'update:split': (_val) => true,
        'update:pane1Collapsed': (_val) => true,
        'update:pane2Collapsed': (_val) => true,
        'pane1-toggle': (_collapsed) => true,
        'pane2-toggle': (_collapsed) => true,
    },
    // #endregion
    // #region Data
    // Internal fields are prefixed to avoid name collision with the new props.
    data() {
        const splitInit = this.modelValue?.split ?? this.split ?? this.initialSplit;
        const p1Init = this.modelValue?.pane1Collapsed ?? this.pane1Collapsed ?? false;
        const p2Init = this.modelValue?.pane2Collapsed ?? this.pane2Collapsed ?? false;
        return {
            currentSplit: splitInit,
            savedSplit: splitInit,
            p1Collapsed: p1Init,
            p2Collapsed: p2Init,
            isDragging: false,
            isAnimating: false,
        };
    },
    // #endregion
    // #region Watch
    watch: {
        // ── Full-state v-model ──────────────────────────────────────────
        modelValue: {
            deep: true,
            handler(val) {
                if (!val) {
                    return;
                }
                if (val.split && val.split !== this.currentSplit) {
                    this.currentSplit = val.split;
                    this.savedSplit = val.split;
                }
                if (val.pane1Collapsed !== this.p1Collapsed || val.pane2Collapsed !== this.p2Collapsed) {
                    this.p1Collapsed = val.pane1Collapsed;
                    this.p2Collapsed = val.pane2Collapsed;
                }
            },
        },
        // ── Individual v-model:split ────────────────────────────────────
        split(val) {
            if (val === null || val === this.currentSplit)
                return;
            this.currentSplit = val;
            if (!this.p1Collapsed && !this.p2Collapsed)
                this.savedSplit = val;
        },
        // ── Individual v-model:pane1Collapsed ──────────────────────────
        pane1Collapsed(val) {
            if (val === null || val === this.p1Collapsed)
                return;
            if (val) {
                this.savedSplit = this.currentSplit;
                this.p2Collapsed = false;
                this.p1Collapsed = true;
            }
            else {
                this.p1Collapsed = false;
                this.currentSplit = this.savedSplit;
            }
        },
        // ── Individual v-model:pane2Collapsed ──────────────────────────
        pane2Collapsed(val) {
            if (val === null || val === this.p2Collapsed)
                return;
            if (val) {
                this.savedSplit = this.currentSplit;
                this.p1Collapsed = false;
                this.p2Collapsed = true;
            }
            else {
                this.p2Collapsed = false;
                this.currentSplit = this.savedSplit;
            }
        },
        // ── Controlled visibility props ─────────────────────────────────
        pane1Visible: {
            immediate: true,
            handler(val) {
                if (val === null)
                    return;
                const shouldCollapse = !val;
                if (shouldCollapse === this.p1Collapsed)
                    return;
                if (shouldCollapse) {
                    this.savedSplit = this.currentSplit;
                    this.p2Collapsed = false;
                    this.p1Collapsed = true;
                }
                else {
                    this.p1Collapsed = false;
                    this.currentSplit = this.savedSplit;
                }
                this.emitState();
            },
        },
        pane2Visible: {
            immediate: true,
            handler(val) {
                if (val === null)
                    return;
                const shouldCollapse = !val;
                if (shouldCollapse === this.p2Collapsed)
                    return;
                if (shouldCollapse) {
                    this.savedSplit = this.currentSplit;
                    this.p1Collapsed = false;
                    this.p2Collapsed = true;
                }
                else {
                    this.p2Collapsed = false;
                    this.currentSplit = this.savedSplit;
                }
                this.emitState();
            },
        },
    },
    // #endregion
    // #region Computed
    computed: {
        pane1Style() {
            if (this.p1Collapsed) {
                return { flex: '0 0 0px', overflow: 'hidden', minWidth: 0, minHeight: 0 };
            }
            if (this.p2Collapsed) {
                return { flex: '1 0 0px', overflow: 'hidden', minWidth: 0, minHeight: 0 };
            }
            return { flex: `${this.currentSplit} 1 0px`, overflow: 'hidden', minWidth: 0, minHeight: 0 };
        },
        pane2Style() {
            if (this.p2Collapsed) {
                return { flex: '0 0 0px', overflow: 'hidden', minWidth: 0, minHeight: 0 };
            }
            if (this.p1Collapsed) {
                return { flex: '1 0 0px', overflow: 'hidden', minWidth: 0, minHeight: 0 };
            }
            return { flex: `${100 - this.currentSplit} 1 0px`, overflow: 'hidden', minWidth: 0, minHeight: 0 };
        },
        btnIcon1() {
            if (this.direction === 'horizontal') {
                return this.p1Collapsed ? 'bi-chevron-right' : 'bi-chevron-left';
            }
            return this.p1Collapsed ? 'bi-chevron-down' : 'bi-chevron-up';
        },
        dividerHidden() {
            if (!this.hideDividerWhenCollapsed)
                return false;
            return this.p1Collapsed || this.p2Collapsed;
        },
        btnIcon2() {
            if (this.direction === 'horizontal') {
                return this.p2Collapsed ? 'bi-chevron-left' : 'bi-chevron-right';
            }
            return this.p2Collapsed ? 'bi-chevron-up' : 'bi-chevron-down';
        },
    },
    // #endregion
    // #region Methods
    methods: {
        // #region Core
        emitState() {
            this.$emit('update:modelValue', {
                split: this.currentSplit,
                pane1Collapsed: this.p1Collapsed,
                pane2Collapsed: this.p2Collapsed,
            });
            this.$emit('update:split', this.currentSplit);
            this.$emit('update:pane1Collapsed', this.p1Collapsed);
            this.$emit('update:pane2Collapsed', this.p2Collapsed);
        },
        togglePane1() {
            this.isAnimating = true;
            if (this.p1Collapsed) {
                this.p1Collapsed = false;
                this.currentSplit = this.savedSplit;
            }
            else {
                this.savedSplit = this.currentSplit;
                this.p2Collapsed = false;
                this.p1Collapsed = true;
            }
            this.$emit('pane1-toggle', this.p1Collapsed);
            this.emitState();
            setTimeout(() => {
                this.isAnimating = false;
            }, 220);
        },
        togglePane2() {
            this.isAnimating = true;
            if (this.p2Collapsed) {
                this.p2Collapsed = false;
                this.currentSplit = this.savedSplit;
            }
            else {
                this.savedSplit = this.currentSplit;
                this.p1Collapsed = false;
                this.p2Collapsed = true;
            }
            this.$emit('pane2-toggle', this.p2Collapsed);
            this.emitState();
            setTimeout(() => {
                this.isAnimating = false;
            }, 220);
        },
        onDividerPointerDown(e) {
            this.isDragging = true;
            const container = this.$refs.container;
            const target = e.currentTarget;
            // Keep cursor consistent while dragging outside the divider
            const cursor = this.direction === 'horizontal' ? 'col-resize' : 'row-resize';
            document.documentElement.style.cursor = cursor;
            document.documentElement.style.userSelect = 'none';
            target.setPointerCapture(e.pointerId);
            let debounceTimer;
            const onMove = (ev) => {
                const rect = container.getBoundingClientRect();
                let pct;
                if (this.direction === 'horizontal') {
                    pct = ((ev.clientX - rect.left) / rect.width) * 100;
                }
                else {
                    pct = ((ev.clientY - rect.top) / rect.height) * 100;
                }
                this.currentSplit = Math.max(this.minPaneSize, Math.min(100 - this.minPaneSize, pct));
                this.savedSplit = this.currentSplit;
                this.p1Collapsed = false;
                this.p2Collapsed = false;
                clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => this.emitState(), 300);
            };
            const onUp = () => {
                this.isDragging = false;
                document.documentElement.style.cursor = '';
                document.documentElement.style.userSelect = '';
                target.removeEventListener('pointermove', onMove);
                target.removeEventListener('pointerup', onUp);
                clearTimeout(debounceTimer);
                this.emitState();
            };
            target.addEventListener('pointermove', onMove);
            target.addEventListener('pointerup', onUp);
        },
        // #endregion
    },
    // #endregion
});
// #endregion
